import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import Header from "../../Components/Header/Header";
import Footer from "../../Components/Footer/Footer";
import Loader from "../../Components/Loader/Loader";
import NotFound from "../NotFound/NotFound";
import { playerUrl, sportUrl } from "../../Utils/Constants";
import { errorMessage, getTeam } from "../../Utils/api";
import "./Catalogue.css";

const PAGE = 60;

/**
 * `/sports/{sport-slug}/{team-slug}` — a team and its roster.
 *
 * The sport segment is load-bearing, not decoration: 878 teams share only 395
 * distinct names, so "alabama-crimson-tide" alone is ambiguous between
 * football and the two basketball programmes.
 */
export default function TeamPage() {
  const { sportSlug, teamSlug } = useParams();
  const [data, setData] = useState(null);
  const [players, setPlayers] = useState([]);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setSkip(0);
    setPlayers([]);
  }, [sportSlug, teamSlug]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getTeam(sportSlug, teamSlug, { limit: PAGE, skip })
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
        setPlayers((prev) =>
          skip === 0 ? payload.players : [...prev, ...payload.players]
        );
      })
      .catch((error) => {
        if (cancelled) return;
        if (error?.response?.status === 404) setNotFound(true);
        else toast.error(errorMessage(error, "Could not load this team"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sportSlug, teamSlug, skip]);

  useEffect(() => {
    if (data?.team) {
      document.title = `${data.team.team_name} ${data.team.sport_name} Roster · NIL-Collab`;
    }
  }, [data]);

  if (notFound) return <NotFound />;

  const team = data?.team;
  const hasMore = team ? players.length < team.roster_count : false;

  return (
    <div>
      <Header />
      {loading ? <Loader /> : null}

      {team ? (
        <div className="cat-page">
          <nav className="cat-crumbs">
            <Link to="/sports">Sports</Link>
            <span>/</span>
            <Link to={sportUrl(team.sport_slug)}>{team.sport_name}</Link>
            <span>/</span>
            <span>{team.team_name}</span>
          </nav>

          <div className="cat-team-header">
            {team.team_logo_url ? (
              <img src={team.team_logo_url} alt="" aria-hidden="true" />
            ) : null}
            <div>
              <h1 className="cat-title">{team.team_name}</h1>
              <p className="cat-sub">
                {[team.conference, team.sport_name].filter(Boolean).join(" · ")}
                {" · "}
                {team.roster_count} athletes
              </p>
            </div>
          </div>

          {players.length === 0 && !loading ? (
            <p className="cat-empty">No athletes are currently listed for this team.</p>
          ) : (
            <div className="cat-roster-grid">
              {players.map((p) => (
                <Link key={p.player_id} to={playerUrl(p)} className="cat-player-card">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.player_name} />
                  ) : (
                    <div className="cat-player-fallback" aria-hidden="true">
                      {(p.player_name || "?").charAt(0)}
                    </div>
                  )}
                  <span className="cat-player-name">{p.player_name}</span>
                  <span className="cat-player-meta">
                    {[
                      p.jersey_number ? `#${p.jersey_number}` : null,
                      p.position,
                      p.player_class,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </Link>
              ))}
            </div>
          )}

          {hasMore ? (
            <div className="cat-more">
              <button onClick={() => setSkip(players.length)} disabled={loading}>
                {loading ? "Loading…" : `Show more (${team.roster_count - players.length} left)`}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <Footer />
    </div>
  );
}
